import { useState } from 'react'
import { Outlet } from 'react-router-dom'

import {
  AtsVacanciesBulkPersistAiAssignmentsDocument,
  AtsVacanciesTableDocument,
  AtsVacancyAssignmentSuggestionsCountDocument,
  AtsVacancyAssignmentSuggestionsDocument,
} from 'generated/resources/graphql'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { AutoAssignReviewTable, AutoAssignSuggestion } from './AutoAssignReviewTable'
import { VacanciesTable } from './Table'

const VacanciesPage = () => {
  const [showReview, setShowReview] = useState(false)
  const [autoAssignData, setAutoAssignData] = useState<AutoAssignSuggestion[] | null>(null)

  const { data: suggestionsCountData } = useQuery(AtsVacancyAssignmentSuggestionsCountDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const suggestionsCount =
    suggestionsCountData?.ats.vacancyAssignmentSuggestionsConnection.totalCount ?? 0

  const [fetchSuggestions] = useLazyQuery(AtsVacancyAssignmentSuggestionsDocument, {
    fetchPolicy: 'network-only',
  })

  const [bulkPersistAiAssignments, { client }] = useMutation(
    AtsVacanciesBulkPersistAiAssignmentsDocument,
    {
      extractorFn: (result) => result.atsMutations.bulkPersistAiAssignmentsAtsVacancy,
    }
  )

  const handleAutoAssign = async (vacancyIds?: string[]) => {
    setAutoAssignData(null)
    setShowReview(true)

    try {
      const { data } = await fetchSuggestions({
        variables: vacancyIds ? { vacancyIds } : undefined,
      })

      if (data) {
        const suggestions: AutoAssignSuggestion[] =
          data.ats.vacancyAssignmentSuggestionsConnection.nodes.map((node) => ({
            id: node.vacancy.id,
            vacancyName: node.vacancy.name,
            status: node.vacancy.status,
            statusLabel: node.vacancy.statusLabel,
            suggestedRecruiter: node.suggestedRecruiter ?? null,
            suggestedRecruiterId: node.suggestedRecruiter?.id ?? null,
            suggestedHiringManager: node.suggestedHiringManager ?? null,
            suggestedHiringManagerId: node.suggestedHiringManager?.id ?? null,
            suggestedJobOpening: node.suggestedJobPosting?.title ?? null,
            suggestedJobPostingId: node.suggestedJobPosting?.id ?? null,
            lineManager: node.vacancy.lineManager ?? null,
            teamName: node.vacancy.team?.name ?? null,
            locationName: node.vacancy.location?.name ?? null,
            deadline: node.vacancy.deadline ?? null,
            vacancyType: node.vacancy.vacancyType ?? null,
            vacancyTypeLabel: node.vacancy.vacancyTypeLabel ?? null,
            originType: node.vacancy.originType ?? null,
            originTypeLabel: node.vacancy.originTypeLabel ?? null,
          }))

        setAutoAssignData(suggestions)
      } else {
        setShowReview(false)
      }
    } catch {
      setShowReview(false)
      setAutoAssignData(null)
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.vacancies.auto_assign_review.error_message'),
      })
    }
  }

  const handleConfirm = async (vacancyIds: string[]) => {
    if (!autoAssignData) return

    const assignments = vacancyIds
      .map((vacancyId) => {
        const suggestion = autoAssignData.find((s) => s.id === vacancyId)
        if (!suggestion) return null

        return {
          vacancyId,
          recruiterId: suggestion.suggestedRecruiterId,
          hiringManagerId: suggestion.suggestedHiringManagerId,
          jobPostingId: suggestion.suggestedJobPostingId,
        }
      })
      .filter((a): a is NonNullable<typeof a> => a !== null)

    const result = await bulkPersistAiAssignments({
      variables: { assignments },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.vacancies.auto_assign_review.error_message'),
      })
    })

    if (handled.success) {
      void client.refetchQueries({
        include: [AtsVacanciesTableDocument, AtsVacancyAssignmentSuggestionsCountDocument],
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.vacancies.auto_assign_review.success_message'),
      })

      setShowReview(false)
      setAutoAssignData(null)
    }
  }

  const handleCancel = () => {
    setShowReview(false)
    setAutoAssignData(null)
  }

  return (
    <ResponsivePage.Body variant="full" showHeader={false}>
      {showReview ? (
        <AutoAssignReviewTable
          data={autoAssignData}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      ) : (
        <VacanciesTable
          suggestionsCount={suggestionsCount}
          onAutoAssign={() => handleAutoAssign()}
          onAutoAssignSelected={(ids) => handleAutoAssign(ids)}
        />
      )}
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default VacanciesPage
